import React, { FC, useState, useEffect, useMemo } from "react";
import StayCardH from "components/StayCardH/StayCardH";
import { GoogleMap, useLoadScript, InfoWindow } from "@react-google-maps/api";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import Checkbox from "shared/Checkbox/Checkbox";
import TabFilters from "./TabFilters";
import TabFiltersMobile from "./TabFiltersMobile";
import Heading2 from "components/Heading/Heading2";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "redux/store";
import Pagination from "shared/Pagination/Pagination";
import { OverlayView } from "@react-google-maps/api";
import StayCard from "components/StayCard/StayCard";
import { useNavigate } from "react-router-dom";
import { clearSearchFilters } from "redux/reducers/Search/SearchSlice"; // You'll need to create this action
import { setSearchData } from "redux/reducers/Search/SearchSlice";
//import { searchCategory } from "../../redux/reducers/Properties/PropertiesSlice";
import { getAllProperties, searchCategory } from "../../redux/reducers/Properties/PropertiesSlice";

export interface SectionGridHasMapProps { }

const SectionGridHasMap: FC<SectionGridHasMapProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [currentHoverID, setCurrentHoverID] = useState<string | number>(-1);
  const [showFullMapFixed, setShowFullMapFixed] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [page, setPage] = useState(1);
  
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY || "",
    libraries: ["places"],
  });

  
  
  const storedSearchData = useSelector(
    (state: RootState) => state.search.searchedData
  );

  const { latitude, longitude, searchedResult } = storedSearchData || {};
  const properties = searchedResult?.properties || [];

  // Default to Lima city center when no search location is set
  const LIMA_DEFAULT = { lat: -12.0464, lng: -77.0428 };

  // Reactive map center: prefer search lat/lng, then first loaded property coords,
  // then fall back to Lima default. Updates whenever properties or search data changes.
  const modifiedLocation = useMemo(() => {
    if (latitude && longitude) {
      return { lat: Number(latitude), lng: Number(longitude) };
    }
    const firstWithCoords = properties.find(
      (p: any) => p.latitude && p.longitude
    );
    if (firstWithCoords) {
      return { lat: Number(firstWithCoords.latitude), lng: Number(firstWithCoords.longitude) };
    }
    return LIMA_DEFAULT;
  }, [latitude, longitude, properties]);


// Normalise any API payload into the consistent shape the map/list expects
  const normalisePayload = (payload: any) => ({
    properties: payload?.properties || [],
    total_count: payload?.total_count || payload?.meta?.total_count || payload?.properties?.length || 0,
    total_pages: payload?.total_pages || payload?.meta?.total_pages || 1,
    current_page: payload?.current_page || payload?.meta?.current_page || 1,
    success: payload?.success,
  });

// Inside the component, add this search trigger function
const triggerInitialSearch = async () => {
  const params = new URLSearchParams();
  
  if (latitude) params.append("q[latitude]", latitude.toString());
  if (longitude) params.append("q[longitude]", longitude.toString());
  params.append("page", "1");
  
  const searchQuery = params.toString();
  
  try {
    const action = await dispatch(searchCategory(searchQuery));
    
    if (action.payload?.success) {
      dispatch(
        setSearchData({
          ...storedSearchData,
          searchedResult: normalisePayload(action.payload),
        })
      );
    }
  } catch (error) {
    console.error("Search error:", error);
  }
};

// Trigger search on mount when we have location but no results
useEffect(() => {
  if (latitude && longitude && !searchedResult) {
    triggerInitialSearch();
  }
}, []); // Empty dependency - only run once on mount

// Re-fetch properties when page changes
useEffect(() => {
  if (page === 1) return; // page 1 is handled by initial load effects
  const params = new URLSearchParams();
  const sd = storedSearchData || {};
  if (sd.latitude) params.append("q[latitude]", sd.latitude.toString());
  if (sd.longitude) params.append("q[longitude]", sd.longitude.toString());
  if (sd.locationData?.neighborhood) params.append("q[neighborhood]", sd.locationData.neighborhood);
  if (sd.locationData?.city) params.append("q[city]", sd.locationData.city);
  if (sd.priceMin != null) params.append("q[min_price]", sd.priceMin.toString());
  if (sd.priceMax != null) params.append("q[max_price]", sd.priceMax.toString());
  if (sd.bedroom) params.append("q[total_bedrooms_gteq]", sd.bedroom.toString());
  if (sd.bathroom) params.append("q[total_bathrooms_gteq]", sd.bathroom.toString());
  if (sd.roomTypeId) params.append("q[room_type_ids]", sd.roomTypeId);
  if (Array.isArray(sd.propertyCategories)) {
    sd.propertyCategories.forEach((id: any) => params.append("q[property_category_ids][]", id));
  }
  if (sd.propertyTypeId) {
    sd.propertyTypeId.split(",").filter(Boolean).forEach((id: string) => params.append("q[property_type_ids][]", id));
  }
  if (Array.isArray(sd.selectedAmenitiesId)) {
    sd.selectedAmenitiesId.forEach((id: any) => params.append("q[amenity_ids][]", id));
  }
  if (Array.isArray(sd.feature)) {
    sd.feature.forEach((id: any) => params.append("q[feature_ids][]", id));
  }
  if (sd.startDate) params.append("q[availability_start_lteq]", sd.startDate.split("T")[0]);
  if (sd.endDate) params.append("q[availability_end_gteq]", sd.endDate.split("T")[0]);
  params.append("page", page.toString());

  dispatch(searchCategory(params.toString())).then((action: any) => {
    if (action.payload?.success) {
      dispatch(
        setSearchData({
          ...storedSearchData,
          searchedResult: normalisePayload(action.payload),
        })
      );
      // Scroll results list back to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  });
}, [page]);

useEffect(() => {
  if (!searchedResult) {
    const fetchAll = async () => {
      const action = await dispatch(getAllProperties(1));
      if (action.payload) {
        // getAllProperties returns { properties, meta, data, success }
        // Normalise into the same shape searchCategory uses so the map
        // and pagination work correctly in both cases.
        const payload = action.payload;
        const normalised = {
          properties: payload.properties || [],
          total_count: payload.meta?.total_count || payload.properties?.length || 0,
          total_pages: payload.meta?.total_pages || 1,
          current_page: payload.meta?.current_page || 1,
          success: payload.success,
        };
        dispatch(
          setSearchData({
            ...storedSearchData,
            searchedResult: normalised,
          })
        );
      }
    };
    fetchAll();
  }
}, [searchedResult]);

// At the top of your component, add this function
const handleClearFilters = async () => {
  console.log("🧹 Clearing all filters...");
  
  // Clear all search data in Redux
  dispatch(
    setSearchData({
      startDate: null,
      endDate: null,
      latitude: null,
      longitude: null,
      locationData: null,
      label: "",
      searchedResult: null,
      propertyTypeId: "",
      propertyCategories: [],
      selectedAmenitiesId: [],
      feature: [],
      roomTypeId: "",
      priceMin: 0,
      priceMax: 5000,
      bedroom: "",
      bathroom: "",
      guestCount: 1,
      totalGuests: 1,
    })
  );

  // Fetch ALL properties without any filters
  const params = new URLSearchParams();
  params.append("page", "1");
  params.append("q[min_price]", "0");
  params.append("q[max_price]", "5000");

  const searchQuery = params.toString();
  console.log("🔍 Fetching all properties:", searchQuery);

  try {
    const action = await dispatch(searchCategory(searchQuery));

    if (action.payload?.success) {
      console.log("✅ All properties loaded:", action.payload);
      dispatch(
        setSearchData({
          startDate: null,
          endDate: null,
          latitude: null,
          longitude: null,
          locationData: null,
          label: "",
          searchedResult: normalisePayload(action.payload),
          propertyTypeId: "",
          propertyCategories: [],
          selectedAmenitiesId: [],
          feature: [],
          roomTypeId: "",
          priceMin: 0,
          priceMax: 5000,
          bedroom: "",
          bathroom: "",
          guestCount: 1,
          totalGuests: 1,
        })
      );
      
      // Reset page to 1
      setPage(1);
    } else {
      console.error("❌ Failed to load properties:", action.payload);
    }
  } catch (error) {
    console.error("❌ Error loading properties:", error);
  }
};

  // ✅ Handle Browse All Properties
  const handleBrowseAll = () => {
    navigate('/');
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-white dark:from-neutral-900 dark:to-neutral-800">
        <div className="text-center p-8 max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-orange-500 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-neutral-100 mb-3">Map Service Unavailable</h3>
          <p className="text-gray-600 dark:text-neutral-400 mb-6">We're having trouble loading the map. Please check your connection and refresh the page.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-white dark:from-neutral-900 dark:to-neutral-800">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-orange-100 dark:border-neutral-700 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-t-primary-600 dark:border-t-primary-500 rounded-full animate-spin"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-neutral-100 mb-2">Finding your perfect stay</h3>
          <p className="text-gray-600 dark:text-neutral-400">Loading properties in your area...</p>
        </div>
      </div>
    );
  }

  const mapContainerStyle = {
    width: "100%",
    height: "100%",
  };

  const modernMapStyles = [
    {
      featureType: "poi",
      elementType: "all",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "poi.business",
      elementType: "all",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "poi.park",
      elementType: "geometry.fill",
      stylers: [{ color: "#b8e0a8" }, { visibility: "on" }]
    },
    {
      featureType: "poi.park",
      elementType: "labels",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ lightness: 30 }]
    },
    {
      featureType: "road.highway",
      elementType: "geometry.fill",
      stylers: [{ color: "#ffd4a8" }]
    },
    {
      featureType: "water",
      elementType: "geometry.fill",
      stylers: [{ color: "#a8d8ea" }]
    },
    {
      featureType: "landscape",
      elementType: "geometry.fill",
      stylers: [{ color: "#fafafa" }]
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [{ visibility: "off" }]
    }
  ];

  return (
    <div className="bg-gray-50 dark:bg-neutral-900 min-h-screen">
      {/* STICKY FILTERS */}
      <div className="sticky top-0 sm:top-[88px] z-30 bg-white dark:bg-neutral-800 shadow-sm">
        {/* ✅ LOCATION DISPLAY - ABOVE FILTERS */}
  {storedSearchData?.locationData && (
    <div className="px-4 sm:px-6 lg:px-8 pt-3">
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#F56948]/10 to-[#F56948]/5 rounded-xl border border-[#F56948]/20">
        <svg className="w-5 h-5 text-[#F56948] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Searching in</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {storedSearchData.locationData.neighborhood 
              ? `${storedSearchData.locationData.neighborhood}, ${storedSearchData.locationData.city}`
              : storedSearchData.locationData.city || storedSearchData.label}
          </span>
        </div>
      </div>
    </div>
  )}
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3 border-b-2 border-orange-100 dark:border-neutral-700">
          {/* Show mobile filter on small screens, desktop filters on large screens */}
          <div className="lg:hidden">
            <TabFiltersMobile page={page} onPageReset={() => setPage(1)} />
          </div>
          <div className="hidden lg:block">
            <TabFilters page={page} onPageReset={() => setPage(1)} />
          </div>
        </div>
      </div>

      {/* MAIN CONTAINER */}
      <div className="relative flex">
        
        {/* RESULTS LIST */}
        <div className="w-full xl:w-[50%] 2xl:w-[52%] flex-shrink-0">
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            
            {/* HEADER - COMPACT */}
            <div className="mb-4">
              {properties?.length > 0 ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-neutral-100">
                      {searchedResult?.total_count || searchedResult?.meta?.total_count || properties.length} {properties.length === 1 ? 'Property' : 'Properties'}
                    </h1>
                    {(searchedResult?.total_pages || searchedResult?.meta?.total_pages) > 1 && (
                      <span className="text-sm text-gray-500 dark:text-neutral-400">
                        · Page {page} of {searchedResult?.total_pages || searchedResult?.meta?.total_pages}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <h1 className="text-lg font-semibold text-gray-900 dark:text-neutral-100">Search Results</h1>
              )}
            </div>

            {/* PROPERTIES GRID */}
            {properties?.length > 0 ? (
              <>
                <div className="space-y-4 pb-6">
                  {properties.map((item: any) => (
                    <div
                      key={item.id}
                      onMouseEnter={() => setCurrentHoverID(item.id)}
                      onMouseLeave={() => setCurrentHoverID(-1)}
                      className="transform transition-all duration-200 hover:-translate-y-1"
                    >
                      <StayCardH data={item} />
                    </div>
                  ))}
                </div>

                {/* PAGINATION */}
                {(searchedResult?.total_pages || searchedResult?.meta?.total_pages) > 1 && (
                  <div className="flex mt-10 mb-12 justify-center items-center pt-8 border-t border-gray-200 dark:border-neutral-700">
                    <Pagination
                      currentPage={page}
                      totalPages={searchedResult?.total_pages || searchedResult?.meta?.total_pages}
                      onPageChange={setPage}
                    />
                  </div>
                )}
              </>
            ) : (
              // PROFESSIONAL EMPTY STATE
              <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-700 p-8 sm:p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-20 sm:w-24 h-20 sm:h-24 mx-auto mb-6 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-full flex items-center justify-center">
                    <svg className="w-10 sm:w-12 h-10 sm:h-12 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-neutral-100 mb-3">
                    No properties found
                  </h3>
                  
                  <p className="text-sm sm:text-base text-gray-600 dark:text-neutral-400 mb-6 sm:mb-8 leading-relaxed">
                    We couldn't find any properties matching your search criteria. Try adjusting your filters, expanding your search area, or modifying your dates.
                  </p>
                  
                  <div className="bg-orange-50 dark:bg-primary-900/20 border border-orange-100 dark:border-primary-800/30 rounded-xl p-4 sm:p-6 mb-6 text-left">
                    <h4 className="font-semibold text-gray-900 dark:text-neutral-100 mb-3 flex items-center gap-2 text-sm sm:text-base">
                      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      Try these suggestions
                    </h4>
                    <ul className="space-y-2 text-xs sm:text-sm text-gray-700 dark:text-neutral-300">
                      <li className="flex items-start gap-2">
                        <span className="text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0">•</span>
                        <span>Remove some filters to see more options</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0">•</span>
                        <span>Search in nearby cities or neighborhoods</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0">•</span>
                        <span>Adjust your move-in dates for more availability</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0">•</span>
                        <span>Consider different property types</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
  {/* Orange button with white text */}
  <button 
    onClick={handleClearFilters}
    className="px-4 sm:px-6 py-2.5 sm:py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md text-sm sm:text-base"
  >
    Clear All Filters
  </button>
  
  {/* Light gray button with DARK text */}
  <button 
    onClick={handleBrowseAll}
    className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-lg border-2 border-gray-300 transition-all duration-200 text-sm sm:text-base"
  >
    Browse All Properties
  </button>
</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* MAP TOGGLE BUTTON - Mobile */}
        {!showFullMapFixed && properties?.length > 0 && (
          <button
            className="flex xl:hidden items-center justify-center fixed bottom-6 left-1/2 transform -translate-x-1/2 px-5 sm:px-6 py-3 sm:py-3.5 bg-primary-600 hover:bg-primary-700 text-white shadow-2xl rounded-full z-40 gap-2 text-xs sm:text-sm font-bold transition-all duration-200 hover:scale-105"
            onClick={() => setShowFullMapFixed(true)}
          >
            <svg className="w-4 sm:w-5 h-4 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z" clipRule="evenodd" />
            </svg>
            Show Map
          </button>
        )}

        {/* MAP SECTION */}
        <div
          className={`xl:flex-1 xl:sticky xl:top-[136px] xl:self-start xl:h-[calc(100vh-136px)] ${
            showFullMapFixed ? "fixed inset-0 z-50" : "hidden xl:block"
          }`}
        >
          {showFullMapFixed && (
            <button
              onClick={() => setShowFullMapFixed(false)}
              className="absolute top-4 sm:top-6 left-4 sm:left-6 z-50 w-10 h-10 bg-white dark:bg-neutral-800 rounded-full shadow-xl flex items-center justify-center hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors border border-gray-200 dark:border-neutral-700"
            >
              <svg className="w-5 h-5 text-gray-700 dark:text-neutral-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          <div className="w-full h-full xl:rounded-2xl overflow-hidden shadow-lg border-0 xl:border border-gray-200 dark:border-neutral-700">
            
            {/* MAP CONTROLS */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
              <div className="bg-white dark:bg-neutral-800 rounded-full shadow-lg px-4 sm:px-5 py-2.5 sm:py-3 border-2 border-orange-100 dark:border-neutral-700 hover:border-orange-200 dark:hover:border-neutral-600 transition-colors">
                <Checkbox
                  className="text-xs sm:text-sm font-medium"
                  name="search-on-move"
                  label="Search as I move"
                />
              </div>
            </div>

            {/* INFO HINT */}
            <div className="hidden sm:block absolute bottom-28 right-4 z-10 bg-white dark:bg-neutral-800 rounded-lg shadow-lg px-4 py-2.5 text-xs text-gray-700 dark:text-neutral-300 border border-gray-200 dark:border-neutral-700 max-w-[180px]">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="leading-tight">Click property markers to view details</span>
              </div>
            </div>

            {/* GOOGLE MAP */}
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={12}
              center={modifiedLocation}
              options={{
                zoomControl: true,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
                styles: modernMapStyles,
                gestureHandling: 'greedy',
                clickableIcons: false,
                disableDefaultUI: false,
              }}
            >
              {/* PROPERTY MARKERS */}
              {searchedResult?.properties?.map((property: any) => (
                <React.Fragment key={property.id}>
                  <OverlayView
                    position={{
                      lat: Number(property.latitude),
                      lng: Number(property.longitude),
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <button
                      onMouseEnter={() => setCurrentHoverID(property.id)}
                      onMouseLeave={() => setCurrentHoverID(-1)}
                      onClick={() => setSelectedProperty(property)}
                      style={{
                        backgroundColor: currentHoverID === property.id || selectedProperty?.id === property.id ? '#f5694b' : '#ffffff',
                        color: currentHoverID === property.id || selectedProperty?.id === property.id ? '#ffffff' : '#171717',
                        borderColor: currentHoverID === property.id || selectedProperty?.id === property.id ? '#ffffff' : '#a3a3a3',
                      }}
                      className={`relative px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap transition-all duration-200 border-2 shadow-lg ${
                        currentHoverID === property.id || selectedProperty?.id === property.id
                          ? "scale-110 shadow-2xl z-20"
                          : "hover:scale-105"
                      }`}
                    >
                      ${property.price_per_month ? property.price_per_month.toLocaleString() : "Contact"}
                    </button>
                  </OverlayView>

                  {/* INFO WINDOW */}
                  {selectedProperty && selectedProperty.id === property.id && (
                    <InfoWindow
                      position={{
                        lat: Number(property.latitude),
                        lng: Number(property.longitude),
                      }}
                      onCloseClick={() => setSelectedProperty(null)}
                      options={{
                        pixelOffset: new window.google.maps.Size(0, -10),
                        maxWidth: 320
                      }}
                    >
                      <div className="w-72 sm:w-80 -m-3 p-2">
                        <StayCard data={property} />
                      </div>
                    </InfoWindow>
                  )}
                </React.Fragment>
              ))}
            </GoogleMap>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionGridHasMap;